import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { Storage } from '@ionic/storage';
import { HttpClient } from '@angular/common/http';
import {
  Platform,
  ToastController,
  LoadingController,
  NavController,
  AlertController
} from '@ionic/angular';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  AUTH_ERRORS,
  USER_ROLES,
  USER_STATUS
} from '../constants/auth.constants';
import { User } from '../catalogs/catalogs.model';
import { ProfileService } from '../profile/profile.service';
import { LoyaltyCardsService } from '../catalogs/loyalty-card.service';
import { GENERAL_RULES } from '../constants/app.constants';
import { CATALOG_RULES } from '../constants/catalog.constants';
import { TranslocoService } from '@ngneat/transloco';

const TOKEN_KEY = 'jwt-token';
const USER_DATA = 'user';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private uri = environment.url;
  private webUrl = environment.webUrl;
  private userData = new BehaviorSubject(null);

  constructor(
    private storage: Storage,
    private http: HttpClient,
    private platform: Platform,
    private toastCtrl: ToastController,
    private loadingCtrl: LoadingController,
    private navCtrl: NavController,
    private profileService: ProfileService,
    private loyaltyCardsService: LoyaltyCardsService,
    private alertCtrl: AlertController,
    private translocoService: TranslocoService
  ) {
    this.platform.ready().then(() => {
      this.initStorage();
      this.loadStoredToken();
    });
  }

async initStorage() {
  await this.storage.create(); 
  this.loadStoredToken();
}
  loadStoredToken() {
    this.storage.get(USER_DATA).then(response => {
      if (response) {
        this.userData.next(response);
      }
    });
  }

  doActivate() {
    this.storage.get(USER_DATA).then(response => {
      if (response) {
        this.userData.next({ ...response, status: USER_STATUS.ACTIVE });
      }
    });
  }

  storeConstants(constants) {
    this.storage.set(
      CATALOG_RULES.HIGHEST_MENU_PRICE,
      Number(constants.catalog.higherMenuPrice)
    );
    this.storage.set(
      GENERAL_RULES.BOOKING_CATALOG_MAXIMUM,
      constants.booking.maximumCatalog
    );
    this.storage.set(
      GENERAL_RULES.BOOKING_DAILY_MAXIMUM,
      constants.booking.maximumDaily
    );
    this.storage.set(
      GENERAL_RULES.LOYALTY_REWARD_EXPIRATION,
      constants.loyaltyCards.expirationRedeemedInDays
    );
    this.storage.set(
      GENERAL_RULES.LOYALTY_CARD_EXPIRATION,
      constants.loyaltyCards.expirationInDays
    );
    this.storage.set(
      GENERAL_RULES.LOYALTY_CARD_MAXIMUM,
      constants.loyaltyCards.maximum
    );
  }

  doLogin(token, data, url: string, loader) {
    if (data.role === USER_ROLES.USER) {
      this.storeConstants(data.constants);

      this.storage.set(USER_DATA, data).then(() => this.userData.next(data));
      loader.dismiss();

      return this.storage.set(TOKEN_KEY, token).then(() => {
        this.loyaltyCardsService.fetchCards().subscribe();
        this.sendNotificationToken();
        this.navCtrl.navigateRoot(url);
      });
    } else {
      let message;
      switch (data.role) {
        case USER_ROLES.MANAGER:
          message = this.translocoService.translate('auth.managerAccessError');
          break;
        default:
          message = this.translocoService.translate('auth.adminAccessError');
      }
      this.toastCtrl
        .create({
          message,
          duration: 2000,
          buttons: [
            {
              text: this.translocoService.translate('common.close'),
              role: 'cancel',
            }
          ],
        })
        .then(toast => {
          toast.present();
          loader.dismiss();
        });
    }
  }

  async login(
    username: string,
    password: string,
    previousURL: string,
    forceLogin?: boolean
  ) {
    return this.http
      .post(this.uri + '/login', {
        username,
        password,
        role: USER_ROLES.USER,
        forceLogin
      })
      .toPromise();
  }

  async facebookLogin(accessToken, previousURL?, forceLogin?: boolean) {
    const loader = await this.loadingCtrl.create();
    await loader.present();
    this.http
      .post(this.uri + '/login/facebook', { accessToken, forceLogin, role: USER_ROLES.USER })
      .subscribe(
        (resp: any) => {
          const { success } = resp;
          const url = previousURL ? previousURL : '/catalogs';
          if (success) {
            const { token, data } = resp.result;
            this.doLogin(token, data, url, loader);
          } else {
            this.toastCtrl
              .create({
                message: this.translocoService.translate('auth.invalidUserError'),
                duration: 2000,
                buttons: [
                  {
                    text: this.translocoService.translate('common.close'),
                    role: 'cancel',
                  }
                ],
              })
              .then(toast => {
                toast.present();
                loader.dismiss();
              });
          }
        },
        resp => {
          if (
            !forceLogin &&
            resp.error &&
            resp.error.code === 'message.sessionLimit'
          ) {
            loader.dismiss();
            this.alertCtrl
              .create({
                header: this.translocoService.translate('auth.sessionLimitTitle'),
                message: this.translocoService.translate('auth.sessionLimitMessage'),
                buttons: [
                  {
                    text: this.translocoService.translate('auth.sessionLimitButton'),
                    handler: () => {
                      this.facebookLogin(accessToken, previousURL, true);
                    }
                  },
                  { text: this.translocoService.translate('common.cancel'), role: 'cancel' }
                ]
              })
              .then(alert => alert.present());
          } else {
            this.toastCtrl
              .create({
                message: this.translocoService.translate('auth.accessError'),
                duration: 2000,
                buttons: [
                  {
                    text: this.translocoService.translate('common.close'),
                    role: 'cancel',
                  }
                ],
              })
              .then(toast => {
                toast.present();
                loader.dismiss();
              });
            loader.dismiss();
          }
        }
      );
  }

  async googleLogin(accessToken, previousURL?, forceLogin?: boolean) {
    const loader = await this.loadingCtrl.create();
    await loader.present();
    this.http
      .post(this.uri + '/login/google', { accessToken, forceLogin, role: USER_ROLES.USER })
      .subscribe(
        (resp: any) => {
          const { success } = resp;
          const url = previousURL ? previousURL : '/catalogs';
          if (success) {
            const { token, data } = resp.result;
            this.doLogin(token, data, url, loader);
          } else {
            this.toastCtrl
              .create({
                message: this.translocoService.translate('auth.invalidUserError'),
                duration: 2000,
                buttons: [
                  {
                    text: this.translocoService.translate('common.close'),
                    role: 'cancel',
                  }
                ],
              })
              .then(toast => {
                toast.present();
                loader.dismiss();
              });
          }
        },
        resp => {
          if (
            !forceLogin &&
            resp.error &&
            resp.error.code === 'message.sessionLimit'
          ) {
            loader.dismiss();
            this.alertCtrl
              .create({
                header: this.translocoService.translate('auth.sessionLimitTitle'),
                message: this.translocoService.translate('auth.sessionLimitMessage'),
                buttons: [
                  {
                    text: this.translocoService.translate('auth.sessionLimitButton'),
                    handler: () => {
                      this.googleLogin(accessToken, previousURL, true);
                    }
                  },
                  { text: this.translocoService.translate('common.cancel'), role: 'cancel' }
                ]
              })
              .then(alert => alert.present());
          } else {
            this.toastCtrl
              .create({
                message: this.translocoService.translate('auth.accessError'),
                duration: 2000,
                buttons: [
                  {
                    text: this.translocoService.translate('common.close'),
                    role: 'cancel',
                  }
                ],
              })
              .then(toast => {
                toast.present();
                loader.dismiss();
              });
            loader.dismiss();
          }
        }
      );
  }

  async appleLogin(data, previousURL?, forceLogin?: boolean) {
    const loader = await this.loadingCtrl.create();
    await loader.present();
    this.http
      .post(this.uri + '/login/apple', { ...data, forceLogin, role: USER_ROLES.USER })
      .subscribe(
        (resp: any) => {
          const { success } = resp;
          const url = previousURL ? previousURL : '/catalogs';
          if (success) {
            const { token, data } = resp.result;
            this.doLogin(token, data, url, loader);
          } else {
            this.toastCtrl
              .create({
                message: this.translocoService.translate('auth.invalidUserError'),
                duration: 2000,
                buttons: [
                  {
                    text: this.translocoService.translate('common.close'),
                    role: 'cancel',
                  }
                ],
              })
              .then(toast => {
                toast.present();
                loader.dismiss();
              });
          }
        },
        resp => {
          if (
            !forceLogin &&
            resp.error &&
            resp.error.code === 'message.sessionLimit'
          ) {
            loader.dismiss();
            this.alertCtrl
              .create({
                header: this.translocoService.translate('auth.sessionLimitTitle'),
                message: this.translocoService.translate('auth.sessionLimitMessage'),
                buttons: [
                  {
                    text: this.translocoService.translate('auth.sessionLimitButton'),
                    handler: () => {
                      this.appleLogin(data, previousURL, true);
                    }
                  },
                  { text: this.translocoService.translate('common.cancel'), role: 'cancel' }
                ]
              })
              .then(alert => alert.present());
          } else {
            const backendErrorMessage = resp?.error?.message || null;
            const backendErrorCode = resp?.error?.code || null;
             let message = this.translocoService.translate('auth.accessError');
              if (backendErrorCode && backendErrorCode !== 'unknown') {
                message += ` (${backendErrorCode})`;
              }
            
              if (backendErrorMessage && backendErrorMessage !== 'Access denied') {
                message = backendErrorMessage;
              }
            this.toastCtrl
              .create({
                message: this.translocoService.translate('auth.accessError'),
                duration: 2000,
                buttons: [
                  {
                    text: this.translocoService.translate('common.close'),
                    role: 'cancel',
                  }
                ],
              })
              .then(toast => {
                toast.present();
                loader.dismiss();
              });
            loader.dismiss();
          }
        }
      );
  }

  async signUp(newUser) {
    const loader = await this.loadingCtrl.create();
    await loader.present();

    this.http
      .post(this.uri + '/sign-up', {
        ...newUser,
        link: `${this.webUrl}/auth/activate/:hash`
      })
      .subscribe((resp: any) => {
        const { success } = resp;
        if (success) {
          const { token, data } = resp.result;
          this.doLogin(token, data, '/catalogs', loader);
        } else {
          const { extra } = resp;
          let message = '';
          switch (extra['0']) {
            case AUTH_ERRORS.USER_EXISTS:
              message = this.translocoService.translate('auth.emailExistsError');
              break;
            default:
              message = this.translocoService.translate('common.error');
          }
          this.toastCtrl
            .create({
              message,
              duration: 2500,
              buttons: [
                {
                  text: this.translocoService.translate('common.close'),
                  role: 'cancel',
                }
              ]
            })
            .then(toast => {
              toast.present();
              loader.dismiss();
            });
        }
      });
  }

  recoverPassword(value) {
    return this.http
      .post(this.uri + '/recovery-password', {
        ...value,
        link: `${this.webUrl}/auth/recover/:hash`
      })
      .pipe(
        map((resp: any) => {
          if (!resp.success) {
            throw new Error(resp.extra[0]);
          }

          return resp.result;
        })
      );
  }

  changePassword(value) {
    return this.http.post(`${this.uri}/change-password?role=${USER_ROLES.USER}`, value).pipe(
      map((resp: any) => {
        if (!resp.success) {
          throw new Error(resp.extra[0]);
        }

        return resp.result;
      })
    );
  }

  refresh() {
    return this.http.get(`${this.uri}/refresh-token?role=${USER_ROLES.USER}`).pipe(
      tap(async (resp: any) => {
        const { success } = resp;
        if (success) {
          const { token } = resp.result;
          await this.storage.set(TOKEN_KEY, token);
          this.sendNotificationToken();
        }
      })
    );
  }

  activate(hash) {
    return this.http.get(this.uri + `/activate/${hash}`).pipe(
      map((resp: any) => {
        const { success } = resp;

        if (!success) {
          throwError(resp.extra[0]);
        }

        const { token, data } = resp.result;

        try {
          return this.storage.set(USER_DATA, data).then(() => {
            this.userData.next(data);
            return this.storage.set(TOKEN_KEY, token).then(() => {
              this.sendNotificationToken();
              return true;
            });
          });
        } catch (err) {
          this.toastCtrl
            .create({
              message: this.translocoService.translate('common.error'),
              duration: 2500,
              buttons: [
                {
                  text: this.translocoService.translate('common.close'),
                  role: 'cancel',
                }
              ],
            })
            .then(toast => {
              toast.present();
            });
        }
      })
    );
  }

  logout() {
    this.userIsAuthenticated.then(value => {
      if (value) {
        this.http.get(this.uri + '/logout').subscribe(
          () => {
            this.removeStoredData();
          },
          err => {
            this.removeStoredData();
          }
        );
      } else {
        this.removeStoredData();
      }
    });
  }

  private removeStoredData() {
    this.storage.remove(TOKEN_KEY).then(() => {
      this.userData.next(null);
    });
    this.storage.remove(USER_DATA);
    this.profileService.clearProfile();
  }

  sendNotificationToken() {
    this.storage.get('notif-token').then(token => {
      if (token) {
        let type = '';

        if (this.platform.is('android')) {
          type = 'TYPE_ANDROID';
        } else if (this.platform.is('ios') || this.platform.is('ipad')) {
          type = 'TYPE_IOS';
        }

        this.http
          .post(this.uri + '/api/v1/device', { token, type, isClient: true })
          .pipe(
            tap((resp: any) => {
              if (!resp.success) {
                throw new Error(resp.extra[0]);
              }
              return resp.result.item;
            })
          )
          .toPromise();
      }
    });
  }

  get userIsAuthenticated(): Promise<User> {
    return this.storage.get(USER_DATA);
  }

  get user(): Observable<User> {
    return this.userData.asObservable();
  }
}
